
import { useState } from 'react';
import Logo from './Logo';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Instagram, Facebook } from 'lucide-react';
import { openWhatsApp } from '@/utils/whatsapp';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { trackSubscribe, trackContact } from '@/utils/analytics';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedEmail || !trimmedEmail.includes('@') || trimmedEmail.length > 255) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert({ email: trimmedEmail });

      if (error) {
        if (error.code === '23505') {
          // Unique constraint violation - already subscribed
          toast.info('You are already subscribed!');
        } else {
          throw error;
        }
      } else {
        // Track Subscribe event for Facebook Pixel
        trackSubscribe();
        toast.success('Thanks for subscribing!');
      }
      setEmail('');
    } catch (error: any) {
      console.error('Newsletter subscription error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-secondary mt-16 md:mt-0">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <Logo />
            <p className="mt-4 text-muted-foreground">Bringing nature's beauty to your home.</p>
          </div>
          <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-semibold text-foreground">Company</h3>
              <ul className="mt-4 space-y-2">
                <li><a href="/about" className="text-muted-foreground hover:text-primary">About Us</a></li>
                <li><a href="/sustainability" className="text-muted-foreground hover:text-primary">Sustainability</a></li>
                <li><a href="/careers" className="text-muted-foreground hover:text-primary">Careers</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Support</h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <button 
                    onClick={() => openWhatsApp("918360249017", "Hi! I need help with my order.")}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    Chat with us
                  </button>
                </li>
                <li><a href="/faq" className="text-muted-foreground hover:text-primary">FAQs</a></li>
                <li><a href="/plant-care" className="text-muted-foreground hover:text-primary">Plant Care</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Follow Us</h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <a href="https://www.instagram.com/paudhify/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
                    <Instagram className="h-4 w-4" />
                    Instagram
                  </a>
                </li>
                <li>
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
                    <Facebook className="h-4 w-4" />
                    Facebook
                  </a>
                </li>
              </ul>
            </div>
            <div className="col-span-2 sm:col-span-2 md:col-span-1">
              <h3 className="font-semibold text-foreground">Join our newsletter</h3>
              <p className="mt-4 text-muted-foreground">Get plant care tips and exclusive offers.</p>
              <form onSubmit={handleNewsletterSubmit} className="mt-4 flex flex-col gap-2">
                <Input 
                  type="email" 
                  placeholder="Your email" 
                  className="bg-background" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </Button>
              </form>
            </div>
          </div>
        </div>
        <div className="mt-12 border-t pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-muted-foreground text-sm">
          <div className="flex flex-wrap justify-center md:justify-start gap-6">
            <a href="/privacy" className="hover:text-primary transition-colors">Privacy Policy</a>
            <a href="/cookie-policy" className="hover:text-primary transition-colors">Cookie Policy</a>
            <a href="/terms" className="hover:text-primary transition-colors">Terms of Use</a>
            <a href="/shipping" className="hover:text-primary transition-colors">Shipping Policy</a>
            <a href="/accessibility" className="hover:text-primary transition-colors">Accessibility Statement</a>
          </div>
          <div className="text-center md:text-right">
            &copy; {new Date().getFullYear()} Paudhify. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
